import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface UnsavedChangesForm {
  store: {
    state: any
    subscribe: (observer: { next?: () => void }) => { unsubscribe: () => void }
  }
  state: { values: any }
}

/**
 * Block navigation when the form has unsaved changes.
 * Compares current form values against a clean snapshot.
 * Call markClean() after form.reset() to update the snapshot.
 * Returns { isDirty, markClean, blocker } — render <UnsavedChangesDialog> with the blocker.
 */
export function useUnsavedChanges(form: UnsavedChangesForm) {
  const isDirtyRef = useRef(false)
  const lastDirtyRef = useRef(false)
  const snapshotRef = useRef('')
  const suppressRef = useRef(false)
  const [isDirty, setIsDirty] = useState(false)

  const markClean = useCallback((values?: unknown) => {
    // Suppress subscriber notifications for the current microtask queue flush —
    // form.reset() can fire the subscriber synchronously or via an internal
    // flushSync before we get a chance to settle, so we gate it here.
    suppressRef.current = true
    snapshotRef.current = JSON.stringify(values ?? form.state.values)
    isDirtyRef.current = false
    lastDirtyRef.current = false
    setIsDirty(false)
    queueMicrotask(() => { suppressRef.current = false })
  }, [form])

  useEffect(() => {
    snapshotRef.current = JSON.stringify(form.state.values)
    isDirtyRef.current = false
    lastDirtyRef.current = false
    setIsDirty(false)
    const sub = form.store.subscribe({
      next: () => {
        if (suppressRef.current) return
        const dirty =
          JSON.stringify(form.state.values) !== snapshotRef.current
        isDirtyRef.current = dirty
        if (dirty !== lastDirtyRef.current) {
          lastDirtyRef.current = dirty
          setIsDirty(dirty)
        }
      },
    })
    return () => sub.unsubscribe()
  }, [form])

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname
  )

  return { isDirty, markClean, blocker }
}
